import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';
import { Section } from '@/components/Section';
import { principles } from '@/content/approach';
import { services } from '@/content/services';
import { work } from '@/content/work';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function Home() {
  useDocumentMeta({ title: 'Kando', path: '/' });

  return (
    <>
      {/* Hero */}
      <Box
        component="section"
        sx={{ pt: { xs: 8, md: 16 }, pb: { xs: 8, md: 14 }, position: 'relative' }}
      >
        <Container>
          {/* The width constraint sits on the heading, not the Stack. `ch` resolves
              against the element's own font-size, so on a parent using body type it
              would compute against 17px and crush the display heading to a column. */}
          <Stack spacing={4}>
            <Typography variant="h1" component="h1" sx={{ maxWidth: { xs: '100%', md: 680 } }}>
              {/* The explicit space matters: JSX strips whitespace that ends in a
                  newline, so without it the heading's accessible name - and anything
                  copied from the page - reads "ideashappen". */}
              We make ideas{' '}
              {/* The payoff word gets the brand yellow behind it rather than on it -
                  yellow text on paper is 1.5:1 and unreadable. */}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: '0.18em',
                  mx: '-0.06em',
                  borderRadius: '0.12em',
                  transform: 'rotate(-1.2deg)',
                }}
              >
                happen
              </Box>
            </Typography>
          </Stack>

          <Stack spacing={4} sx={{ maxWidth: '56ch', mt: 5 }}>
            <Typography variant="subtitle1" color="text.secondary">
              We&rsquo;re a software and AI consultancy. Bring us the thing you&rsquo;ve been
              meaning to build - the half-finished prototype, the spreadsheet that has outgrown
              itself, the model that never made it out of the notebook - and we&rsquo;ll get it live
              and working.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Tell us your idea
              </Button>
              <Button component={RouterLink} to="/services" variant="outlined" size="large">
                See what we do
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Services */}
      <Section
        eyebrow="What we do"
        title="Four things we're good at"
        lead="We're deliberately not a full-service agency. These are the areas where a small senior team runs rings around a big one."
        tinted
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: { xs: 4, md: 6 },
          }}
        >
          {services.map((service, index) => (
            <Stack key={service.id} spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                {String(index + 1).padStart(2, '0')}
              </Typography>
              <Typography variant="h4" component="h3">
                {service.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {service.summary}
              </Typography>
            </Stack>
          ))}
        </Box>

        <Button
          component={RouterLink}
          to="/services"
          endIcon={<ArrowForwardIcon />}
          sx={{ mt: 6, px: 0 }}
        >
          All services
        </Button>
      </Section>

      {/* Approach */}
      <Section eyebrow="How we work" title="How we like to work">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 4, md: 6 },
          }}
        >
          {principles.map((principle) => (
            <Stack
              key={principle.id}
              spacing={1.5}
              sx={{ borderLeft: 2, borderColor: 'secondary.main', pl: 3 }}
            >
              <Typography variant="h4" component="h3">
                {principle.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {principle.body}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Section>

      {/* Work teaser */}
      <Section eyebrow="Selected work" title="Some things we've built" tinted>
        <Stack spacing={5}>
          {work.map((item) => (
            <Stack
              key={item.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1, md: 6 }}
              sx={{ pb: 4, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box sx={{ minWidth: { md: 220 } }}>
                <Typography variant="h4" component="h3">
                  {item.title}
                </Typography>
                <Typography variant="overline" color="text.secondary">
                  {item.kind}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '60ch' }}>
                {item.response}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          component={RouterLink}
          to="/work"
          endIcon={<ArrowForwardIcon />}
          sx={{ mt: 4, px: 0 }}
        >
          More detail
        </Button>
      </Section>

      {/* Closing call to action */}
      <Section>
        <Stack spacing={3} sx={{ maxWidth: 560 }}>
          <Typography variant="h2" component="h2">
            Got an idea waiting?
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            We&rsquo;d love to hear it. And if we&rsquo;re not the right people for it, we&rsquo;ll
            say so happily - and point you at someone who is.
          </Typography>
          <Box>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Hello Kando
            </Button>
          </Box>
        </Stack>
      </Section>
    </>
  );
}
